import {
  Box,
  Button,
  Checkbox,
  Input,
  Option,
  Select,
  Text,
  Textarea,
} from "@saleor/macaw-ui-next";
import { Maximize2, Minimize2 } from "lucide-react";
import { PointerEvent as ReactPointerEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { LabelFieldContent } from "./LabelFieldContent";
import {
  addImageElement,
  addMarkElement,
  addSingletonElement,
  addTextBox,
  LabelBox,
  LabelElement,
  LabelElementKind,
  LabelLayout,
  LabelTextAlign,
  MARK_IDS,
  MarkId,
  removeElement,
  SINGLETON_ELEMENT_KINDS,
  TEXT_ELEMENT_KINDS,
  textFloorPt,
  updateElement,
} from "./layout";
import {
  labelCanvasEditorMessages,
  labelFieldLabelMessages,
  labelPrintDialogMessages,
} from "./messages";
import { contentElement, resolveNameSkuBoxes } from "./resolveNameSkuBoxes";
import { LabelData } from "./types";
import { useMeasuredNameHeight } from "./useMeasuredNameHeight";

const messages = { ...labelPrintDialogMessages, ...labelCanvasEditorMessages };

interface LabelCanvasEditorProps {
  item: LabelData;
  layout: LabelLayout;
  widthMm: number;
  heightMm: number;
  onChange: (layout: LabelLayout) => void;
}

const MIN_BOX_PERCENT = 5;
// How close (in % of the canvas) an edge/center needs to be to another one
// before it snaps to it — PowerPoint/Figma-style alignment guides.
const SNAP_THRESHOLD_PERCENT = 1.5;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

interface Guides {
  v: number[];
  h: number[];
}

const NO_GUIDES: Guides = { v: [], h: [] };

// Snaps `value` to the closest of `targets` within SNAP_THRESHOLD_PERCENT,
// returning the delta to apply and the target it snapped to (for the guide
// line), or null if nothing was close enough.
const closestSnap = (
  value: number,
  targets: number[],
): { delta: number; target: number } | null => {
  let best: { delta: number; target: number } | null = null;

  for (const target of targets) {
    const delta = target - value;

    if (
      Math.abs(delta) <= SNAP_THRESHOLD_PERCENT &&
      (!best || Math.abs(delta) < Math.abs(best.delta))
    ) {
      best = { delta, target };
    }
  }

  return best;
};

// Snaps a moving/resizing box against every other element's edges/centers
// plus the canvas's own edges/center. Only one horizontal and one vertical
// snap apply at a time (the closest), matching how slide editors behave.
const snapBox = (
  box: LabelBox,
  mode: "move" | "resize",
  others: LabelBox[],
): { box: LabelBox; guides: Guides } => {
  const xTargets = [0, 50, 100, ...others.flatMap(o => [o.x, o.x + o.width / 2, o.x + o.width])];
  const yTargets = [0, 50, 100, ...others.flatMap(o => [o.y, o.y + o.height / 2, o.y + o.height])];

  if (mode === "resize") {
    const right = closestSnap(box.x + box.width, xTargets);
    const bottom = closestSnap(box.y + box.height, yTargets);

    return {
      box: {
        ...box,
        width: right ? box.width + right.delta : box.width,
        height: bottom ? box.height + bottom.delta : box.height,
      },
      guides: { v: right ? [right.target] : [], h: bottom ? [bottom.target] : [] },
    };
  }

  const movingXCandidates = [box.x, box.x + box.width / 2, box.x + box.width];
  const movingYCandidates = [box.y, box.y + box.height / 2, box.y + box.height];

  const xSnap = movingXCandidates
    .map(candidate => closestSnap(candidate, xTargets))
    .filter((snap): snap is { delta: number; target: number } => snap !== null)
    .sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta))[0];
  const ySnap = movingYCandidates
    .map(candidate => closestSnap(candidate, yTargets))
    .filter((snap): snap is { delta: number; target: number } => snap !== null)
    .sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta))[0];

  return {
    box: {
      ...box,
      x: xSnap ? box.x + xSnap.delta : box.x,
      y: ySnap ? box.y + ySnap.delta : box.y,
    },
    guides: { v: xSnap ? [xSnap.target] : [], h: ySnap ? [ySnap.target] : [] },
  };
};

// What the box would show by default (no override) — used as the content
// override field's placeholder, so it's obvious what typing something will
// replace.
const defaultFieldValue = (
  kind: "name" | "sku" | "price" | "batch",
  item: LabelData,
  intl: ReturnType<typeof useIntl> | null,
): string => {
  switch (kind) {
    case "name":
      return item.name;
    case "sku":
      return item.sku;
    case "price":
      return item.price ? `${item.price.amount} ${item.price.currency}` : "";
    case "batch":
      return item.batchNumber ?? intl?.formatMessage(labelFieldLabelMessages.noBatchNumber) ?? "";
  }
};

// The product's own value, used to prefill the content override field so a
// small tweak doesn't mean retyping everything. Unlike defaultFieldValue it
// never returns a "no value" hint, which would be wrong to edit into the label.
const editableFieldValue = (kind: "name" | "sku" | "price" | "batch", item: LabelData): string =>
  kind === "batch" ? (item.batchNumber ?? "") : defaultFieldValue(kind, item, null);

const fieldLabel = (kind: LabelElementKind, intl: ReturnType<typeof useIntl>) =>
  ({
    image: intl.formatMessage(messages.fieldImage),
    name: intl.formatMessage(messages.fieldName),
    sku: intl.formatMessage(messages.fieldSku),
    price: intl.formatMessage(messages.fieldPrice),
    batch: intl.formatMessage(messages.fieldBatch),
    barcode: intl.formatMessage(messages.fieldBarcode),
    qrcode: intl.formatMessage(messages.fieldQrCode),
    text: intl.formatMessage(messages.fieldText),
    importer: intl.formatMessage(messages.fieldImporter),
    icon: intl.formatMessage(messages.addMarkLabel),
  })[kind];

const markLabel = (markId: MarkId, intl: ReturnType<typeof useIntl>) =>
  ({
    ce: intl.formatMessage(messages.markCe),
    "ce-red": intl.formatMessage(messages.markCeRed),
    ukca: intl.formatMessage(messages.markUkca),
    weee: intl.formatMessage(messages.markWeee),
    rohs: intl.formatMessage(messages.markRohs),
    recycle: intl.formatMessage(messages.markRecycle),
    reach: intl.formatMessage(messages.markReach),
    ghs01: intl.formatMessage(messages.markGhs01),
    ghs02: intl.formatMessage(messages.markGhs02),
    ghs03: intl.formatMessage(messages.markGhs03),
    ghs04: intl.formatMessage(messages.markGhs04),
    ghs05: intl.formatMessage(messages.markGhs05),
    ghs06: intl.formatMessage(messages.markGhs06),
    ghs07: intl.formatMessage(messages.markGhs07),
    ghs08: intl.formatMessage(messages.markGhs08),
    ghs09: intl.formatMessage(messages.markGhs09),
    brand: intl.formatMessage(messages.markBrand),
    "brand-stacked": intl.formatMessage(messages.markBrandStacked),
  })[markId];

interface DragState {
  id: string;
  mode: "move" | "resize";
  startClientX: number;
  startClientY: number;
  startBox: LabelBox;
  startBoxes: Array<{ id: string; box: LabelBox }>;
}

interface SelectionDragState {
  startX: number;
  startY: number;
  baseIds: string[];
}

const ALIGN_OPTIONS: LabelTextAlign[] = ["left", "center", "right"];

const alignLabel = (align: LabelTextAlign, intl: ReturnType<typeof useIntl>) =>
  ({
    left: intl.formatMessage(messages.alignLeft),
    center: intl.formatMessage(messages.alignCenter),
    right: intl.formatMessage(messages.alignRight),
  })[align];

interface HistoryState {
  stack: LabelLayout[];
  index: number;
}

// Groups related controls behind a titled, bordered card — used throughout
// the sidebar so "Fields", "Add element" and "Selected element" read as
// distinct blocks instead of one long unbroken stack of controls.
const Section = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <Box
    display="flex"
    flexDirection="column"
    gap={2}
    padding={3}
    borderWidth={1}
    borderStyle="solid"
    borderColor="default1"
    borderRadius={2}
    style={{
      background: "var(--box-background-default1, #fff)",
      boxShadow: "0 1px 3px rgba(22, 28, 36, 0.05)",
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Text size={2} fontWeight="bold">
        {title}
      </Text>
      {action}
    </Box>
    {children}
  </Box>
);

export const LabelCanvasEditor = ({
  item,
  layout,
  widthMm,
  heightMm,
  onChange,
}: LabelCanvasEditorProps) => {
  const intl = useIntl();
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const selectionDragRef = useRef<SelectionDragState | null>(null);
  const latestLayoutRef = useRef(layout);
  // Fields unchecked/removed during this session, by kind, so re-checking one
  // brings back the same box, style and text instead of a default-sized box.
  const removedSingletonsRef = useRef(new Map<LabelElementKind, LabelElement>());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionBox, setSelectionBox] = useState<LabelBox | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [guides, setGuides] = useState<Guides>(NO_GUIDES);
  const [markToAdd, setMarkToAdd] = useState<MarkId>("ce");
  // Local, per-editing-session undo/redo history. This component only ever
  // mounts while actively editing (see LabelPrintDialog's isEditing branch),
  // so a fresh mount = a fresh session = a fresh history — no reset needed.
  const [history, setHistory] = useState<HistoryState>(() => ({ stack: [layout], index: 0 }));

  const selected =
    selectedIds.size === 1
      ? (layout.elements.find(element => element.id === selectedId) ?? null)
      : null;
  const skuElement = layout.elements.find(element => element.kind === "sku");
  const measuredNameHeight = useMeasuredNameHeight(
    canvasRef,
    layout.elements.find(element => element.kind === "name")?.id,
  );
  const dynamicBoxes = resolveNameSkuBoxes(layout, item, widthMm, heightMm, measuredNameHeight);
  const minFontSizePt = textFloorPt(layout, heightMm);
  const itemImages = item.imageUrls?.length ? item.imageUrls : item.imageUrl ? [item.imageUrl] : [];
  const canUndo = history.index > 0;
  const canRedo = history.index < history.stack.length - 1;

  // Live update (drag, or any change not yet checkpointed into history).
  const applyChange = (next: LabelLayout) => {
    latestLayoutRef.current = next;
    onChange(next);
  };

  // A discrete, undoable action: apply it and checkpoint it into history,
  // dropping any redo branch beyond the current point.
  const commit = (next: LabelLayout) => {
    applyChange(next);
    setHistory(prev => ({
      stack: [...prev.stack.slice(0, prev.index + 1), next],
      index: prev.index + 1,
    }));
  };

  const undo = () => {
    if (!canUndo) {
      return;
    }

    const newIndex = history.index - 1;

    applyChange(history.stack[newIndex]);
    setHistory(prev => ({ ...prev, index: newIndex }));
  };

  const redo = () => {
    if (!canRedo) {
      return;
    }

    const newIndex = history.index + 1;

    applyChange(history.stack[newIndex]);
    setHistory(prev => ({ ...prev, index: newIndex }));
  };

  const rememberRemoved = (removed: LabelElement[]) => {
    removed
      .filter(element => SINGLETON_ELEMENT_KINDS.includes(element.kind))
      .forEach(element => removedSingletonsRef.current.set(element.kind, element));
  };

  const toggleSingleton = (kind: LabelElementKind, checked: boolean) => {
    if (checked) {
      const previous = removedSingletonsRef.current.get(kind);

      commit(
        previous && !layout.elements.some(element => element.kind === kind)
          ? { elements: [...layout.elements, previous] }
          : addSingletonElement(layout, kind),
      );

      return;
    }

    const existing = layout.elements.find(element => element.kind === kind);

    if (existing) {
      rememberRemoved([existing]);
      commit(removeElement(layout, existing.id));

      if (selectedId === existing.id) {
        setSelectedId(null);
        setSelectedIds(new Set());
      }
    }
  };

  const handleAddTextBox = () => {
    const next = addTextBox(layout);

    commit(next);
    setSelectedId(next.elements[next.elements.length - 1].id);
    setSelectedIds(new Set([next.elements[next.elements.length - 1].id]));
  };

  const handleAddImage = () => {
    const next = addImageElement(layout);

    commit(next);
    setSelectedId(next.elements[next.elements.length - 1].id);
    setSelectedIds(new Set([next.elements[next.elements.length - 1].id]));
  };

  const handleAddMark = () => {
    const next = addMarkElement(layout, markToAdd);

    commit(next);
    setSelectedId(next.elements[next.elements.length - 1].id);
    setSelectedIds(new Set([next.elements[next.elements.length - 1].id]));
  };

  const patchElement = (id: string, patch: Partial<Omit<LabelElement, "id" | "kind">>) => {
    commit(updateElement(layout, id, patch));
  };

  const removeSelected = () => {
    if (selectedIds.size === 0) {
      return;
    }

    rememberRemoved(layout.elements.filter(element => selectedIds.has(element.id)));
    commit({ elements: layout.elements.filter(element => !selectedIds.has(element.id)) });
    setSelectedId(null);
    setSelectedIds(new Set());
  };

  // PowerPoint/Figma-style "Align" toolbar for a multi-selection: matches
  // every selected box's edge (or center) to the outermost one among them,
  // in one undo step — the click-driven counterpart to the snap-while-
  // dragging guides above, for lining boxes up precisely without eyeballing
  // a drag.
  const alignSelected = (edge: "left" | "centerX" | "right" | "top" | "centerY" | "bottom") => {
    const selectedElements = layout.elements.filter(element => selectedIds.has(element.id));

    if (selectedElements.length < 2) {
      return;
    }

    const boxes = selectedElements.map(element => element.box);
    let target: number;

    switch (edge) {
      case "left":
        target = Math.min(...boxes.map(box => box.x));
        break;
      case "right":
        target = Math.max(...boxes.map(box => box.x + box.width));
        break;
      case "centerX":
        target =
          (Math.min(...boxes.map(box => box.x)) +
            Math.max(...boxes.map(box => box.x + box.width))) /
          2;
        break;
      case "top":
        target = Math.min(...boxes.map(box => box.y));
        break;
      case "bottom":
        target = Math.max(...boxes.map(box => box.y + box.height));
        break;
      case "centerY":
        target =
          (Math.min(...boxes.map(box => box.y)) +
            Math.max(...boxes.map(box => box.y + box.height))) /
          2;
        break;
    }

    const nextElements = layout.elements.map(element => {
      if (!selectedIds.has(element.id)) {
        return element;
      }

      const { box } = element;

      switch (edge) {
        case "left":
          return { ...element, box: { ...box, x: target } };
        case "right":
          return { ...element, box: { ...box, x: target - box.width } };
        case "centerX":
          return { ...element, box: { ...box, x: target - box.width / 2 } };
        case "top":
          return { ...element, box: { ...box, y: target } };
        case "bottom":
          return { ...element, box: { ...box, y: target - box.height } };
        case "centerY":
          return { ...element, box: { ...box, y: target - box.height / 2 } };
      }
    });

    commit({ elements: nextElements });
  };

  const startBoxSelection = (event: ReactPointerEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    (event.target as Element).setPointerCapture(event.pointerId);

    const startX = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
    const startY = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);

    selectionDragRef.current = { startX, startY, baseIds: Array.from(selectedIds) };
    setSelectionBox({ x: startX, y: startY, width: 0, height: 0 });
  };

  const startDrag = (id: string, mode: "move" | "resize") => (event: ReactPointerEvent) => {
    const element = layout.elements.find(candidate => candidate.id === id);

    if (!element) {
      return;
    }

    if (event.shiftKey && mode === "move") {
      startBoxSelection(event);

      return;
    }

    event.preventDefault();
    event.stopPropagation();
    (event.target as Element).setPointerCapture(event.pointerId);

    const movingIds = mode === "move" && selectedIds.has(id) ? selectedIds : new Set([id]);
    const startBoxes = layout.elements
      .filter(candidate => movingIds.has(candidate.id))
      .map(candidate => ({
        id: candidate.id,
        box: dynamicBoxes.get(candidate.id) ?? candidate.box,
      }));
    const startBox = dynamicBoxes.get(id) ?? element.box;

    setSelectedId(id);
    setSelectedIds(new Set(movingIds));
    dragRef.current = {
      id,
      mode,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startBox,
      startBoxes,
    };
  };

  const handlePointerMove = (event: ReactPointerEvent) => {
    const drag = dragRef.current;
    const rect = canvasRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    if (selectionDragRef.current) {
      const start = selectionDragRef.current;
      const currentX = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      const currentY = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
      const left = Math.min(start.startX, currentX);
      const top = Math.min(start.startY, currentY);

      setSelectionBox({
        x: left,
        y: top,
        width: Math.abs(currentX - start.startX),
        height: Math.abs(currentY - start.startY),
      });

      return;
    }

    if (!drag) {
      return;
    }

    const dxPercent = ((event.clientX - drag.startClientX) / rect.width) * 100;
    const dyPercent = ((event.clientY - drag.startClientY) / rect.height) * 100;
    const { id, mode, startBox } = drag;

    const minX = Math.min(...drag.startBoxes.map(entry => entry.box.x));
    const minY = Math.min(...drag.startBoxes.map(entry => entry.box.y));
    const maxX = Math.max(...drag.startBoxes.map(entry => entry.box.x + entry.box.width));
    const maxY = Math.max(...drag.startBoxes.map(entry => entry.box.y + entry.box.height));
    const groupBox: LabelBox = {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
    const movedX = clamp(dxPercent, -minX, 100 - maxX);
    const movedY = clamp(dyPercent, -minY, 100 - maxY);
    const rawBox: LabelBox =
      mode === "move"
        ? { ...groupBox, x: groupBox.x + movedX, y: groupBox.y + movedY }
        : {
            ...startBox,
            width: clamp(startBox.width + dxPercent, MIN_BOX_PERCENT, 100 - startBox.x),
            height: clamp(startBox.height + dyPercent, MIN_BOX_PERCENT, 100 - startBox.y),
          };

    const movingIds = new Set(drag.startBoxes.map(entry => entry.id));
    const others = layout.elements
      .filter(candidate => !movingIds.has(candidate.id))
      .map(candidate => candidate.box);
    const { box: nextBox, guides: nextGuides } = snapBox(rawBox, mode, others);

    setGuides(nextGuides);

    const snapX = nextBox.x - rawBox.x;
    const snapY = nextBox.y - rawBox.y;
    const nextLayout =
      mode === "resize"
        ? updateElement(layout, id, { box: nextBox })
        : {
            elements: layout.elements.map(candidate => {
              const original = drag.startBoxes.find(entry => entry.id === candidate.id);

              return original
                ? {
                    ...candidate,
                    box: {
                      ...original.box,
                      x: clamp(original.box.x + movedX + snapX, 0, 100 - original.box.width),
                      y: clamp(original.box.y + movedY + snapY, 0, 100 - original.box.height),
                    },
                  }
                : candidate;
            }),
          };

    applyChange(nextLayout);
  };

  const endDrag = (event?: ReactPointerEvent) => {
    if (selectionDragRef.current) {
      const rect = canvasRef.current?.getBoundingClientRect();

      if (rect && event) {
        const start = selectionDragRef.current;
        const endX = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
        const endY = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
        const selection = {
          left: Math.min(start.startX, endX),
          right: Math.max(start.startX, endX),
          top: Math.min(start.startY, endY),
          bottom: Math.max(start.startY, endY),
        };
        const nextIds = new Set(start.baseIds);

        for (const element of layout.elements) {
          const box = element.box;
          const intersects =
            box.x <= selection.right &&
            box.x + box.width >= selection.left &&
            box.y <= selection.bottom &&
            box.y + box.height >= selection.top;

          if (intersects) {
            nextIds.add(element.id);
          }
        }

        setSelectedIds(nextIds);
        setSelectedId(Array.from(nextIds).pop() ?? null);
      }

      selectionDragRef.current = null;
      setSelectionBox(null);

      return;
    }

    if (dragRef.current) {
      const finalLayout = latestLayoutRef.current;

      setHistory(prev => ({
        stack: [...prev.stack.slice(0, prev.index + 1), finalLayout],
        index: prev.index + 1,
      }));
    }

    dragRef.current = null;
    setGuides(NO_GUIDES);
  };

  // Slide-editor-style keyboard shortcuts: Ctrl/Cmd+Z to undo, Ctrl/Cmd+Shift+Z
  // (or Ctrl+Y) to redo, Delete/Backspace to remove the selected element,
  // Escape to deselect. Ignored while typing in a text field so e.g. deleting
  // a character in the content-override textarea doesn't delete the element.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTypingTarget =
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      const isModifierHeld = event.metaKey || event.ctrlKey;

      if (isModifierHeld && event.key.toLowerCase() === "z" && !event.shiftKey) {
        event.preventDefault();
        undo();

        return;
      }

      if (
        (isModifierHeld && event.key.toLowerCase() === "z" && event.shiftKey) ||
        (isModifierHeld && event.key.toLowerCase() === "y")
      ) {
        event.preventDefault();
        redo();

        return;
      }

      if (isTypingTarget) {
        return;
      }

      if (event.key === "Escape") {
        setSelectedId(null);
        setSelectedIds(new Set());

        return;
      }

      if ((event.key === "Delete" || event.key === "Backspace") && selectedIds.size > 0) {
        event.preventDefault();
        removeSelected();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const barcodeElement = layout.elements.find(element => element.kind === "barcode");
  const priceElement = layout.elements.find(element => element.kind === "price");
  const batchElement = layout.elements.find(element => element.kind === "batch");

  const markOptions: Option[] = MARK_IDS.map(markId => ({
    value: markId,
    label: markLabel(markId, intl),
  }));

  return (
    <Box
      display="grid"
      gap={4}
      style={{
        width: "100%",
        gridTemplateColumns: "minmax(0, 1.5fr) minmax(280px, 0.85fr)",
        alignItems: "start",
        ...(isFullscreen
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 10000,
              width: "auto",
              height: "100dvh",
              overflow: "auto",
              padding: 24,
              background: "var(--box-background-default1, #fff)",
              boxSizing: "border-box",
              gridTemplateColumns: "minmax(0, 1fr) minmax(320px, 420px)",
            }
          : {}),
      }}
    >
      <Box display="flex" flexDirection="column" gap={2} style={{ minWidth: 0 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{
            padding: "12px 16px",
            border: "1px solid var(--box-border-neutral-plain-default, #d9dce2)",
            borderRadius: 8,
            background: "var(--box-background-default1, #fff)",
          }}
        >
          <Text size={2} fontWeight="bold">
            {intl.formatMessage(messages.canvasTitle)}
          </Text>
          <Box display="flex" gap={2} alignItems="center">
            <Button variant="secondary" size="small" onClick={undo} disabled={!canUndo}>
              {intl.formatMessage(messages.undo)}
            </Button>
            <Button variant="secondary" size="small" onClick={redo} disabled={!canRedo}>
              {intl.formatMessage(messages.redo)}
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={() => setIsFullscreen(value => !value)}
              title={intl.formatMessage(
                isFullscreen ? messages.exitFullscreenEditor : messages.fullscreenEditor,
              )}
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </Button>
          </Box>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{
            padding: 20,
            background: "var(--box-background-neutral-plain, #f3f4f6)",
            borderRadius: 10,
            border: "1px solid var(--box-border-neutral-plain-default, #e0e3e8)",
          }}
        >
          <Box
            ref={canvasRef}
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            borderRadius={2}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerDown={event => {
              if (event.shiftKey) {
                startBoxSelection(event);
              } else {
                setSelectedId(null);
                setSelectedIds(new Set());
              }
            }}
            style={{
              position: "relative",
              overflow: "hidden",
              width: "100%",
              maxWidth: isFullscreen
                ? `${Math.min(window.innerWidth * 0.58, window.innerHeight * 0.52 * (widthMm / heightMm))}px`
                : 480,
              background: "#fff",
              boxShadow: "0 4px 18px rgba(22, 28, 36, 0.12)",
              aspectRatio: `${widthMm} / ${heightMm}`,
              touchAction: "none",
              // See LabelPreviewCard: keeps cqh-based font sizes proportional to
              // the label regardless of how big this canvas renders on screen.
              containerType: "size",
            }}
            data-label-root
          >
            {layout.elements.map(element => {
              const dynamicBox = dynamicBoxes.get(element.id);
              const renderElement = dynamicBox ? { ...element, box: dynamicBox } : element;

              return (
                <Box
                  key={element.id}
                  data-label-element={element.id}
                  onPointerDown={startDrag(element.id, "move")}
                  style={{
                    position: "absolute",
                    left: `${renderElement.box.x}%`,
                    top: `${renderElement.box.y}%`,
                    width: `${renderElement.box.width}%`,
                    height: `${renderElement.box.height}%`,
                    overflow: "hidden",
                    cursor: "move",
                    outline: selectedIds.has(element.id)
                      ? "2px solid var(--box-background-accent1, #0f7a75)"
                      : "1px solid rgba(92, 103, 116, 0.16)",
                  }}
                >
                  <LabelFieldContent
                    element={contentElement(element, renderElement)}
                    item={item}
                    heightMm={heightMm}
                    widthMm={widthMm}
                    minFontSizePt={minFontSizePt}
                  />
                  {element.id === selectedId && selectedIds.size === 1 && (
                    <Box
                      onPointerDown={startDrag(element.id, "resize")}
                      style={{
                        position: "absolute",
                        right: -4,
                        bottom: -4,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "var(--box-background-accent1, #0f7a75)",
                        cursor: "nwse-resize",
                      }}
                    />
                  )}
                </Box>
              );
            })}
            {guides.v.map(x => (
              <Box
                key={`v-${x}`}
                style={{
                  position: "absolute",
                  left: `${x}%`,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  backgroundColor: "#ff2d78",
                  pointerEvents: "none",
                }}
              />
            ))}
            {guides.h.map(y => (
              <Box
                key={`h-${y}`}
                style={{
                  position: "absolute",
                  top: `${y}%`,
                  left: 0,
                  right: 0,
                  height: 1,
                  backgroundColor: "#ff2d78",
                  pointerEvents: "none",
                }}
              />
            ))}
            {selectionBox && (
              <Box
                style={{
                  position: "absolute",
                  left: `${selectionBox.x}%`,
                  top: `${selectionBox.y}%`,
                  width: `${selectionBox.width}%`,
                  height: `${selectionBox.height}%`,
                  border: "1px dashed var(--box-background-accent1, #0f7a75)",
                  background: "rgba(15, 122, 117, 0.12)",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              />
            )}
          </Box>
        </Box>
        <Text size={1} color="default2">
          {intl.formatMessage(messages.editorHint)}
        </Text>
      </Box>

      <Box display="flex" flexDirection="column" gap={3} style={{ minWidth: 0 }}>
        <Section title={intl.formatMessage(messages.sectionFieldsTitle)}>
          <Box
            display="grid"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 130px), 1fr))",
              gap: 8,
            }}
          >
            {SINGLETON_ELEMENT_KINDS.map(kind => (
              <Box key={kind} display="flex" flexDirection="column" gap={1}>
                <Checkbox
                  checked={layout.elements.some(element => element.kind === kind)}
                  onCheckedChange={checked => toggleSingleton(kind, checked === true)}
                >
                  <Text size={2}>{fieldLabel(kind, intl)}</Text>
                </Checkbox>
                {kind === "barcode" && barcodeElement && (
                  <Box style={{ paddingLeft: 24 }}>
                    <Checkbox
                      checked={Boolean(barcodeElement.barcodeShowText)}
                      onCheckedChange={checked =>
                        patchElement(barcodeElement.id, { barcodeShowText: checked === true })
                      }
                    >
                      <Text size={1} color="default2">
                        {intl.formatMessage(messages.fieldBarcodeText)}
                      </Text>
                    </Checkbox>
                  </Box>
                )}
                {kind === "sku" && skuElement && (
                  <Box style={{ paddingLeft: 24 }}>
                    <Checkbox
                      checked={Boolean(skuElement.showLabel)}
                      onCheckedChange={checked =>
                        patchElement(skuElement.id, { showLabel: checked === true })
                      }
                    >
                      <Text size={1} color="default2">
                        {intl.formatMessage(messages.showFieldLabel, {
                          label: intl.formatMessage(messages.fieldSku),
                        })}
                      </Text>
                    </Checkbox>
                  </Box>
                )}
                {kind === "price" && priceElement && (
                  <Box style={{ paddingLeft: 24 }}>
                    <Checkbox
                      checked={Boolean(priceElement.showLabel)}
                      onCheckedChange={checked =>
                        patchElement(priceElement.id, { showLabel: checked === true })
                      }
                    >
                      <Text size={1} color="default2">
                        {intl.formatMessage(messages.showFieldLabel, {
                          label: intl.formatMessage(messages.fieldPrice),
                        })}
                      </Text>
                    </Checkbox>
                  </Box>
                )}
                {kind === "batch" && batchElement && (
                  <Box style={{ paddingLeft: 24 }}>
                    <Checkbox
                      checked={Boolean(batchElement.showLabel)}
                      onCheckedChange={checked =>
                        patchElement(batchElement.id, { showLabel: checked === true })
                      }
                    >
                      <Text size={1} color="default2">
                        {intl.formatMessage(messages.showFieldLabel, {
                          label: intl.formatMessage(messages.fieldBatch),
                        })}
                      </Text>
                    </Checkbox>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Section>

        <Section title={intl.formatMessage(messages.sectionAddElementTitle)}>
          <Button variant="secondary" size="small" onClick={handleAddImage}>
            {intl.formatMessage(messages.addImage)}
          </Button>
          <Button variant="secondary" size="small" onClick={handleAddTextBox}>
            {intl.formatMessage(messages.addTextBox)}
          </Button>
          <Box
            display="grid"
            style={{ gridTemplateColumns: "minmax(0, 1fr) auto", alignItems: "end", gap: 8 }}
          >
            <Select
              label={intl.formatMessage(messages.addMarkLabel)}
              options={markOptions}
              value={markOptions.find(option => option.value === markToAdd) ?? null}
              onChange={option => setMarkToAdd(option.value as MarkId)}
              size="small"
              style={{ width: "100%" }}
            />
            <Button variant="secondary" size="small" onClick={handleAddMark}>
              {intl.formatMessage(messages.addMarkLabel)}
            </Button>
          </Box>
        </Section>

        {selectedIds.size > 1 ? (
          <Section
            title={intl.formatMessage(messages.selectedElementsCount, { count: selectedIds.size })}
            action={
              <Button variant="tertiary" size="small" onClick={removeSelected}>
                {intl.formatMessage(messages.removeElement)}
              </Button>
            }
          >
            <Text size={1} color="default2">
              {intl.formatMessage(messages.groupSelectionHint)}
            </Text>
            <Box display="flex" flexDirection="column" gap={2}>
              <Text size={1} color="default2">
                {intl.formatMessage(messages.alignSectionLabel)}
              </Text>
              <Box display="flex" gap={1} style={{ flexWrap: "wrap" }}>
                <Button variant="secondary" size="small" onClick={() => alignSelected("left")}>
                  {intl.formatMessage(messages.alignBoxLeft)}
                </Button>
                <Button variant="secondary" size="small" onClick={() => alignSelected("centerX")}>
                  {intl.formatMessage(messages.alignBoxCenterX)}
                </Button>
                <Button variant="secondary" size="small" onClick={() => alignSelected("right")}>
                  {intl.formatMessage(messages.alignBoxRight)}
                </Button>
                <Button variant="secondary" size="small" onClick={() => alignSelected("top")}>
                  {intl.formatMessage(messages.alignBoxTop)}
                </Button>
                <Button variant="secondary" size="small" onClick={() => alignSelected("centerY")}>
                  {intl.formatMessage(messages.alignBoxCenterY)}
                </Button>
                <Button variant="secondary" size="small" onClick={() => alignSelected("bottom")}>
                  {intl.formatMessage(messages.alignBoxBottom)}
                </Button>
              </Box>
            </Box>
          </Section>
        ) : selected ? (
          <Section
            title={
              selected.kind === "icon"
                ? markLabel(selected.markId ?? "ce", intl)
                : fieldLabel(selected.kind, intl)
            }
            action={
              <Button variant="tertiary" size="small" onClick={removeSelected}>
                {intl.formatMessage(messages.removeElement)}
              </Button>
            }
          >
            {selected.kind === "image" && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Text size={1} color="default2">
                  {intl.formatMessage(messages.imageGalleryHint)}
                </Text>
                {itemImages.length > 0 ? (
                  <Box
                    display="grid"
                    style={{ gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))", gap: 8 }}
                  >
                    {itemImages.map((url, index) => (
                      <button
                        key={`${url}-${index}`}
                        type="button"
                        aria-label={intl.formatMessage(messages.imageNumber, { number: index + 1 })}
                        aria-pressed={(selected.imageIndex ?? 0) === index}
                        onClick={() => patchElement(selected.id, { imageIndex: index })}
                        style={{
                          height: 72,
                          padding: 4,
                          borderRadius: 6,
                          border:
                            (selected.imageIndex ?? 0) === index
                              ? "2px solid var(--box-background-accent1, #0f7a75)"
                              : "1px solid var(--box-border-neutral-plain-default, #e0e3e8)",
                          background: "white",
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={url}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </button>
                    ))}
                  </Box>
                ) : (
                  <Text size={1} color="default2">
                    {intl.formatMessage(messages.noProductImages)}
                  </Text>
                )}
              </Box>
            )}
            {selected.kind === "text" && (
              <Textarea
                label={intl.formatMessage(messages.textBoxContentLabel)}
                size="small"
                rows={3}
                value={selected.content ?? ""}
                onChange={e => patchElement(selected.id, { content: e.target.value })}
              />
            )}

            {selected.kind === "importer" && (
              <Box display="flex" flexDirection="column" gap={3}>
                <Textarea
                  label={intl.formatMessage(messages.importerFieldName)}
                  size="small"
                  rows={1}
                  value={selected.content ?? ""}
                  onChange={e => patchElement(selected.id, { content: e.target.value })}
                />
                <Textarea
                  label={intl.formatMessage(messages.importerFieldAddress)}
                  size="small"
                  rows={2}
                  value={selected.importerAddress ?? ""}
                  onChange={e => patchElement(selected.id, { importerAddress: e.target.value })}
                />
                <Text size={1} color="default2">
                  {intl.formatMessage(messages.fieldImporter)}
                </Text>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Checkbox
                    checked={selected.importerFields?.name !== false}
                    onCheckedChange={checked =>
                      patchElement(selected.id, {
                        importerFields: { ...selected.importerFields, name: checked === true },
                      })
                    }
                  >
                    <Text size={2}>{intl.formatMessage(messages.importerFieldName)}</Text>
                  </Checkbox>
                  <Checkbox
                    checked={selected.importerFields?.address !== false}
                    onCheckedChange={checked =>
                      patchElement(selected.id, {
                        importerFields: { ...selected.importerFields, address: checked === true },
                      })
                    }
                  >
                    <Text size={2}>{intl.formatMessage(messages.importerFieldAddress)}</Text>
                  </Checkbox>
                  <Checkbox
                    checked={selected.importerFields?.email !== false}
                    onCheckedChange={checked =>
                      patchElement(selected.id, {
                        importerFields: { ...selected.importerFields, email: checked === true },
                      })
                    }
                  >
                    <Text size={2}>{intl.formatMessage(messages.importerFieldEmail)}</Text>
                  </Checkbox>
                  <Checkbox
                    checked={selected.importerFields?.phone !== false}
                    onCheckedChange={checked =>
                      patchElement(selected.id, {
                        importerFields: { ...selected.importerFields, phone: checked === true },
                      })
                    }
                  >
                    <Text size={2}>{intl.formatMessage(messages.importerFieldPhone)}</Text>
                  </Checkbox>
                </Box>
              </Box>
            )}

            {(selected.kind === "name" ||
              selected.kind === "sku" ||
              selected.kind === "price" ||
              selected.kind === "batch") && (
              <Box display="flex" flexDirection="column" gap={1}>
                <Textarea
                  label={intl.formatMessage(messages.contentOverrideLabel)}
                  size="small"
                  rows={2}
                  placeholder={defaultFieldValue(selected.kind, item, intl)}
                  value={selected.content ?? editableFieldValue(selected.kind, item)}
                  onChange={e => {
                    const selectedKind = selected.kind as "name" | "sku" | "price" | "batch";
                    const { value } = e.target;

                    // Matching the product's value again means "no override",
                    // so the box keeps following product data.
                    patchElement(selected.id, {
                      content: value === editableFieldValue(selectedKind, item) ? undefined : value,
                    });
                  }}
                />
                {selected.content !== undefined && (
                  <Button
                    variant="tertiary"
                    size="small"
                    onClick={() => patchElement(selected.id, { content: undefined })}
                  >
                    {intl.formatMessage(messages.resetToProductData)}
                  </Button>
                )}
              </Box>
            )}

            {selected.kind === "icon" && (
              <Box display="flex" gap={3} alignItems="flex-end" style={{ flexWrap: "wrap" }}>
                <Select
                  label={intl.formatMessage(messages.markSelectLabel)}
                  options={markOptions}
                  value={markOptions.find(option => option.value === selected.markId) ?? null}
                  onChange={option => patchElement(selected.id, { markId: option.value as MarkId })}
                  size="small"
                />
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text size={1} color="default2">
                    {intl.formatMessage(messages.colorLabel)}
                  </Text>
                  <input
                    type="color"
                    value={selected.style?.color ?? "#000000"}
                    onChange={e =>
                      patchElement(selected.id, {
                        style: { ...selected.style, color: e.target.value },
                      })
                    }
                    style={{
                      width: 32,
                      height: 32,
                      padding: 0,
                      border: "none",
                      background: "none",
                    }}
                  />
                </Box>
              </Box>
            )}

            {TEXT_ELEMENT_KINDS.includes(selected.kind) && (
              <Box display="flex" gap={3} style={{ flexWrap: "wrap" }} alignItems="flex-end">
                <Input
                  label={intl.formatMessage(messages.fontSizeLabel)}
                  type="number"
                  min={5}
                  max={72}
                  size="small"
                  style={{ width: 90 }}
                  value={selected.style?.fontSizePt ?? 9}
                  onChange={e =>
                    patchElement(selected.id, {
                      style: {
                        ...selected.style,
                        fontSizePt: Math.max(5, Number(e.target.value) || 9),
                      },
                    })
                  }
                />
                <Checkbox
                  checked={Boolean(selected.style?.bold)}
                  onCheckedChange={checked =>
                    patchElement(selected.id, {
                      style: { ...selected.style, bold: checked === true },
                    })
                  }
                >
                  <Text size={2}>{intl.formatMessage(messages.boldLabel)}</Text>
                </Checkbox>
                <Box display="flex" gap={1}>
                  {ALIGN_OPTIONS.map(align => (
                    <Button
                      key={align}
                      variant={selected.style?.align === align ? "primary" : "secondary"}
                      size="small"
                      onClick={() =>
                        patchElement(selected.id, { style: { ...selected.style, align } })
                      }
                    >
                      {alignLabel(align, intl)}
                    </Button>
                  ))}
                </Box>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text size={1} color="default2">
                    {intl.formatMessage(messages.colorLabel)}
                  </Text>
                  <input
                    type="color"
                    value={selected.style?.color ?? "#000000"}
                    onChange={e =>
                      patchElement(selected.id, {
                        style: { ...selected.style, color: e.target.value },
                      })
                    }
                    style={{
                      width: 32,
                      height: 32,
                      padding: 0,
                      border: "none",
                      background: "none",
                    }}
                  />
                </Box>
              </Box>
            )}
          </Section>
        ) : null}
      </Box>
    </Box>
  );
};
